import dayjs from 'dayjs';
import React, { useMemo, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';

interface A4ContentProviderProps {
  children: React.ReactNode;
  documentTitle?: string;
  options?: {
    printHeader?: boolean;
    landscape?: boolean;
    showPrint?: boolean;
    showPDF?: boolean;
  };
}

const A4ContentProvider: React.FC<A4ContentProviderProps> = ({
  children,
  documentTitle = 'Document',
  options = {},
}) => {
  const [loading, setLoading] = useState<{ pdf: boolean; print: boolean }>({
    pdf: false,
    print: false,
  });

  const mergedOptions = useMemo(
    () => ({
      printHeader: true,
      landscape: false,
      showPrint: true,
      showPDF: true,
      ...options,
    }),
    [options]
  );

  const contentRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    documentTitle: `${documentTitle}-${dayjs().format('YYYY-MM-DD HH:mm:ss')}`,
    content: () => contentRef.current,
    pageStyle: `
     @page {
      size: A4;
      margin: 10mm !important;
    }
    @media print {
      html, body {
        width: 210mm;
        height: 297mm;
        margin: 0;
        padding: 0;
        overflow: visible;
      }
      * {
        print-color-adjust: exact;
        -webkit-print-color-adjust: exact;
        color-adjust: exact;
      }
      table, tr, td {
        page-break-inside: avoid;
      }
      .page-break-before {
         page-break-before: always;
       }
    }
    `,
    onBeforePrint: async () => {
      setLoading((prev) => ({ ...prev, print: true }));
      return Promise.resolve();
    },
    onAfterPrint: () => setLoading((prev) => ({ ...prev, print: false })),
  });

  const styles = {
    container: {
      maxWidth: '210mm',
      // minHeight: '297mm',
      margin: '0 auto',
      padding: '0mm',
      boxSizing: 'border-box' as const,
      backgroundColor: 'white',
      position: 'relative' as const,
      pageBreakAfter: 'always',
      border: '1px dashed #cccccc',
    },
    content: {
      padding: '0mm',
      boxSizing: 'border-box' as const,
    },
    button: (isLoading: boolean, color: string) => ({
      position: 'absolute' as const,
      top: '1mm',
      zIndex: 1,
      cursor: isLoading ? 'not-allowed' : 'pointer',
      background: isLoading ? '#ccc' : color,
      padding: '0.3em 0.5em',
      borderRadius: '3px',
      border: 'none',
      color: '#000',
      transition: 'font-weight 0.2s ease',
      margin: '5px',
    }),
  };

  return (
    <section style={{ ...styles.container, pageBreakAfter: 'always' as const }}>
      <div ref={contentRef} style={styles.content}>
        {children}
      </div>

      {mergedOptions.showPrint && (
        <button
          onClick={() => handlePrint()}
          disabled={loading.print}
          style={{ ...styles.button(loading.print, '#4CC9F3'), right: '2mm' }}
          aria-label='Print Document'
        >
          <small>{loading.print ? 'PRINTING...' : 'PRINT OUT'}</small>
        </button>
      )}
    </section>
  );
};

export default React.memo(A4ContentProvider);
